import { FC, useCallback, useMemo } from 'react'
import { OnboardingPagePreset } from 'components/features/creditCard/creditCardOnboarding/OnboardingPagePreset/OnboardingPagePreset'
import styles from 'components/features/creditCard/creditCardOnboarding/AddressStepPage/AddressStepPage.styl'
import { FIELD_ADDRESS1_PROPS, FIELD_ADDRESS2_PROPS, FIELD_ZIP_PROPS } from 'constants/fields'
import { Field, isSubmitting, reduxForm, submit } from 'redux-form'
import { ReduxFormProps } from 'interfaces/component'
import TextFieldWrapped from 'components/atoms/inputs/TextFieldWrapped'
import { asyncValidator } from 'helpers/schemaHelpers'
import { shouldAsyncValidate } from 'helpers/formHelpers'
import CityStateField from 'components/atoms/inputs/CityStateField'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { PatchCCOnboardingAddressAction } from 'components/features/creditCard/creditCardStore/actions'
import { addressFormSchema, CC_ONBOARDING_FORM_ADDRESS } from 'components/features/creditCard/creditCardOnboarding/constants'
import Button from 'components/features/pfm/Button/Button'
import { Link } from 'react-router-dom'
import { personDataSelector } from 'components/features/creditCard/creditCardStore/selectors'
import { useNextStep } from 'components/features/creditCard/creditCardOnboarding/useNextStep'
import { FormCommonError } from 'components/features/creditCard/creditCardOnboarding/FormCommonError/FormCommonError'
import { pushActionCreator } from 'actions/navigationActions'
import { useStepNumber } from 'components/features/creditCard/creditCardOnboarding/useStepNumber'

type FormValues = {
  address1: string
  address2: string
  zip5: string
  city: {
    value: number
    label: string
    type: string
  }
}

const EMPTY_OBJ = {}
export const AddressStepPage: FC = () => {
  const nextStep = useNextStep()

  const dispatch = useStoreDispatch()
  const handleSubmit = useCallback<(values: FormValues) => {}>((values) => {

    const { zip5, address1, address2, city } = values
    const { value } = city
    const [, , bldNumber = '', street = ''] = address1.match(/((\d+)\s)?(.*)/) || []

    const person_data = {
      address: {
        zip5,
        city_id: value,
        street,
        refinement: address2,
        number: bldNumber,
      },
    }

    dispatch(new PatchCCOnboardingAddressAction({
      person_data,
      step: nextStep || 'phone',
    }))

    return EMPTY_OBJ
  }, [dispatch, nextStep])

  const handleNextButton = useCallback(() => {
    dispatch(submit(CC_ONBOARDING_FORM_ADDRESS))
  }, [dispatch])

  const prevCCPersonData = useStoreSelector(personDataSelector)

  const formChild = useMemo(() => {
    let initialValues: Partial<FormValues> = {}
    if (prevCCPersonData) {
      const { address } = prevCCPersonData
      const { street, refinement, number: bldNumber, zip5, city } = address
      const { id, name: cityName, state } = city
      const address1 = !!bldNumber ? `${ bldNumber } ${ street }` : `${ street }`
      initialValues = {
        address1: address1.trim(),
        address2: refinement,
        zip5,
        city: {
          value: id,
          label: cityName + ', ' + state.code,
          type: state.code,
        },
      }
    }

    return (
      <>
        <FormWrapped onSubmit={ handleSubmit } initialValues={ initialValues } />
        <div className={ styles.poNote } data-qa-marker='form-note'>
          P.O. boxes are not allowed.<br />
          Currently, we are only available in the United States.
        </div>
      </>
    )
  }, [handleSubmit, prevCCPersonData])

  const isLoading = useStoreSelector((state) => isSubmitting(CC_ONBOARDING_FORM_ADDRESS)(state))

  const handleClose = useCallback(() => {
    if (nextStep) {
      dispatch(pushActionCreator(`/credit-card/onboarding/${ nextStep }`))
    } else {
      dispatch(pushActionCreator(`/credit-card`))
    }
  }, [dispatch, nextStep])

  const stepNo = useStepNumber('address')

  return (
    <OnboardingPagePreset
      step={ stepNo }
      title={ `What's your residential address?` }
      card={ formChild }
      onClose={ handleClose }
      progressPercent={ 5 }
    >

      <Button
        text='Next'
        onClick={ handleNextButton }
        fullWidth
        qaMarker='next-button'
        isSubmitting={ isLoading }
      />
      <Link to='/credit-card'>
        <Button
          text='Cancel'
          style='textOnly'
          fullWidth
          qaMarker='cancel-button'
        />
      </Link>
      <div className={ styles.bottomNote } data-qa-marker='footnotes'>
        We'll ship your card to the address above.
        <br /><br />
        P.O. box, Private Mail Box, business, temporary and non-US based addresses are not accepted.
        <br /><br />
        Status Money is a financial management company, not a bank. Card is issued by Evolve Bank &amp; Trust, Member FDIC pursuant to a license from Visa&trade;.
        <br /><br />
        IMPORTANT INFORMATION ABOUT PROCEDURES FOR OPENING A NEW ACCOUNT: To help the government fight the funding of terrorism and money laundering activities, Federal law requires all financial institutions to obtain, verify and record information that identifies each person who opens an account. What this means for you: When you open an account, we will ask for your name, address, date of birth and other information that will allow us to identify you. We may also ask to see your driver's license or other identifying documents.
        <br /><br />
        Status Money<br />
        79 Madison Ave. Suite 930<br />
        New York, NY 10016<br />
      </div>
    </OnboardingPagePreset>
  )
}

type FormProps =
  & ReduxFormProps<FormValues>
  & {}

const Form: FC<FormProps> = (props) => {
  const { handleSubmit, error } = props

  return (
    <form onSubmit={ handleSubmit }>
      <ul className={ styles.controlList }>
        <li className={ styles.controlListItem }>
          <Field
            name='address1'
            component={ TextFieldWrapped }
            props={{
              ...FIELD_ADDRESS1_PROPS,
              theme: 'snow',
            }}
          />
        </li>
        <li className={ styles.controlListItem }>
          <Field
            name='address2'
            component={ TextFieldWrapped }
            props={{
              ...FIELD_ADDRESS2_PROPS,
              theme: 'snow',
            }}
          />
        </li>
        <li className={ styles.controlListItem }>
          <CityStateField
            name='city'
          />
        </li>
        <li className={ styles.controlListItem }>
          <Field
            name='zip5'
            component={ TextFieldWrapped }
            props={{
              ...FIELD_ZIP_PROPS,
            }}
          />
        </li>
        <FormCommonError error={ error } className={ styles.controlListItem } />
      </ul>
    </form>
  )
}

const FormWrapped = reduxForm<FormValues, FormProps>({
  form: CC_ONBOARDING_FORM_ADDRESS,
  asyncValidate: asyncValidator(addressFormSchema),
  shouldAsyncValidate,
})(Form)
